// "New workspace": name and an optional description. On success the API has
// already made the caller its owner and answers with a token for it, so we
// switch straight into the welcome tour to set it up.

import { useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ApiError, ApiUnreachableError, workspacesApi } from '../lib/api-client'
import { switchToToken } from '../lib/auth'
import { Dialog, Field } from './ui'

export function CreateWorkspaceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const create = useMutation({
    mutationFn: workspacesApi.create,
    onSuccess: (result) => switchToToken(result.token, '/welcome?step=executor'),
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    const trimmed = description.trim()
    create.mutate({ name: name.trim(), ...(trimmed ? { description: trimmed } : {}) })
  }

  function close(): void {
    if (create.isPending) return
    create.reset()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      size="md"
      title="New workspace"
      description="A separate space with its own projects, agents and connections. You become its owner."
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <Field label="Name" htmlFor="new-workspace-name">
          <input
            id="new-workspace-name"
            data-autofocus
            className="field-input"
            value={name}
            maxLength={80}
            required
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Description (optional)" htmlFor="new-workspace-description">
          <textarea
            id="new-workspace-description"
            className="field-input"
            rows={3}
            maxLength={2000}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        {create.isError ? (
          <p
            className="rounded-[10px] border border-danger/30 bg-danger-soft px-3 py-2 text-base text-ink"
            role="alert"
          >
            {create.error instanceof ApiUnreachableError
              ? `${create.error.message} Check that the API is running.`
              : create.error instanceof ApiError
                ? create.error.message
                : `Couldn’t create the workspace: ${(create.error as Error).message}`}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={close} disabled={create.isPending}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={create.isPending || name.trim() === ''}
          >
            {create.isPending ? 'Creating…' : 'Create workspace'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
